import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const evidenceSections = [
  {
    id: 'executive-summary',
    letter: 'A',
    title: 'Executive readiness summary',
    intro: 'Internal evidence candidate for BOSA/FAS preparation. The goal here is to show what idemGO already demonstrates, what is still draft, and what must be validated with BOSA/FAS before any external discussion.',
    items: [
      {
        area: 'Executive summary',
        component: 'Internal digital key concept and current lab flow',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Open digital key demo',
        status: 'Internal lab',
        strength: 'Strong',
        gap: 'Keep the lab guide internal-only and use it as the shared runbook for internal demos.',
      },
      {
        area: 'Executive summary',
        component: 'What is implemented',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Continue proving the end-to-end lab flow with safe claims only.',
      },
      {
        area: 'Executive summary',
        component: 'What is still draft / needs validation',
        linkTo: '/internal/fas-readiness',
        linkLabel: 'Open readiness matrix',
        status: 'Needs BOSA/FAS validation',
        strength: 'Placeholder',
        gap: 'Confirm the recognition path, assurance expectations, and test expectations with BOSA/FAS.',
      },
      {
        area: 'Executive summary',
        component: 'What must be confirmed with BOSA/FAS',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Open audit governance',
        status: 'Draft',
        strength: 'Early',
        gap: 'Prepare a concise question list covering scope, evidence, security, privacy, and operating model.',
      },
    ],
  },
  {
    id: 'identity-proofing',
    letter: 'B',
    title: 'Identity proofing evidence',
    intro: 'Evidence items showing how approved verification evidence flows into the idem identity and the internal identity layer.',
    items: [
      {
        area: 'Identity proofing',
        component: 'KYC/IDV verification',
        linkTo: '/internal/identities',
        linkLabel: 'Open identities',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep verification origin and outcome visible without exposing the raw source evidence.',
      },
      {
        area: 'Identity proofing',
        component: 'ID document capture',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'BOSA evidence pack',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep capture outputs minimized and internal-only.',
      },
      {
        area: 'Identity proofing',
        component: 'MRZ / OCR processing',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'BOSA evidence pack',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Do not expose raw OCR or MRZ payloads in the evidence dossier.',
      },
      {
        area: 'Identity proofing',
        component: 'Selfie / face match',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'BOSA evidence pack',
        status: 'Partial',
        strength: 'Medium',
        gap: 'Document reviewer thresholds, failure handling, and manual override policy.',
      },
      {
        area: 'Identity proofing',
        component: 'Manual review',
        linkTo: '/internal/identities',
        linkLabel: 'Identity operations',
        status: 'Partial',
        strength: 'Medium',
        gap: 'Define queue ownership, escalation path, and second-pair review expectations.',
      },
      {
        area: 'Identity proofing',
        component: 'Identity evidence layer',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Draft',
        strength: 'Early',
        gap: 'Explain which evidence stays internal and how it is retained or deleted.',
      },
      {
        area: 'Identity proofing',
        component: 'Approved verification → idem identity creation',
        linkTo: '/internal/identities',
        linkLabel: 'Open identity operations',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep the linkage path tied to approved verification outcomes only.',
      },
    ],
  },
  {
    id: 'digital-key-account',
    letter: 'C',
    title: 'Digital key account evidence',
    intro: 'Evidence items showing the relationship between idem identity, digital key account lifecycle, and the account-to-login-request flow.',
    items: [
      {
        area: 'Digital key account',
        component: 'idem identity',
        linkTo: '/internal/identities',
        linkLabel: 'Open identities',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Use the identity as the source anchor for the internal digital key flow.',
      },
      {
        area: 'Digital key account',
        component: 'Digital key account',
        linkTo: '/internal/digital-key-accounts',
        linkLabel: 'Open digital key accounts',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep account state, policy, and linking metadata internal only.',
      },
      {
        area: 'Digital key account',
        component: 'Account lifecycle / status',
        linkTo: '/internal/identities',
        linkLabel: 'Identity detail',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Continue tracking draft, active, suspended, and revoked states with safe metadata.',
      },
      {
        area: 'Digital key account',
        component: 'Assurance policy placeholder / summary',
        linkTo: '/internal/digital-key-architecture',
        linkLabel: 'Digital key architecture',
        status: 'Partial',
        strength: 'Medium',
        gap: 'Define how internal readiness maps to future assessment questions without naming an official LoA.',
      },
      {
        area: 'Digital key account',
        component: 'Identity → account linking',
        linkTo: '/internal/identities',
        linkLabel: 'Identity detail',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Preserve the safe linkage pattern and avoid exposing raw evidence in the overview.',
      },
      {
        area: 'Digital key account',
        component: 'Account → login request flow',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Open demo runner',
        status: 'Internal lab',
        strength: 'Strong',
        gap: 'Use the guided demo runner as the internal lab runbook for this flow.',
      },
    ],
  },
  {
    id: 'login-authentication',
    letter: 'D',
    title: 'Login and authentication lab evidence',
    intro: 'Evidence items for the internal login request, approval, return, and safe claims preview flow.',
    items: [
      {
        area: 'Login and authentication lab',
        component: 'OIDC Lab',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Internal lab',
        strength: 'Strong',
        gap: 'Keep the simulator internal-only and limited to safe claims output.',
      },
      {
        area: 'Login and authentication lab',
        component: 'Login request creation',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Open demo runner',
        status: 'Internal lab',
        strength: 'Strong',
        gap: 'Document the lab request state machine and request IDs for internal review only.',
      },
      {
        area: 'Login and authentication lab',
        component: 'Authenticator Preview',
        linkTo: '/internal/authenticator-preview',
        linkLabel: 'Open authenticator preview',
        status: 'Internal lab',
        strength: 'Strong',
        gap: 'Keep approve/deny decisions safe and limited to internal metadata.',
      },
      {
        area: 'Login and authentication lab',
        component: 'Approve / deny flow',
        linkTo: '/internal/authenticator-preview',
        linkLabel: 'Review approvals',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Retain the return-to-lab handoff and avoid adding production OIDC behavior.',
      },
      {
        area: 'Login and authentication lab',
        component: 'Return to OIDC Lab',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Return via demo runner',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep query-param handoff limited to internal request IDs and safe context.',
      },
      {
        area: 'Login and authentication lab',
        component: 'Finalize request',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Finalize in OIDC lab',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Ensure finalization continues to show safe, non-sensitive output only.',
      },
      {
        area: 'Login and authentication lab',
        component: 'Safe claims preview',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Review safe claims',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Do not release raw identity evidence, documents, or regulated identifiers.',
      },
    ],
  },
  {
    id: 'lifecycle-management',
    letter: 'E',
    title: 'Lifecycle management evidence',
    intro: 'Identity and account lifecycle controls needed for a future digital key service, kept as internal operational evidence only.',
    items: [
      {
        area: 'Lifecycle management',
        component: 'Create identity',
        linkTo: '/internal/identities',
        linkLabel: 'Open identity operations',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Continue tying identity creation to approved verification outcomes.',
      },
      {
        area: 'Lifecycle management',
        component: 'Suspend identity',
        linkTo: '/internal/identities',
        linkLabel: 'Identity operations',
        status: 'Partial',
        strength: 'Medium',
        gap: 'Define the suspend state, review path, and reason capture rules.',
      },
      {
        area: 'Lifecycle management',
        component: 'Reactivate identity',
        linkTo: '/internal/identities',
        linkLabel: 'Identity operations',
        status: 'Draft',
        strength: 'Early',
        gap: 'Document approval requirements before any reactivation workflow is considered.',
      },
      {
        area: 'Lifecycle management',
        component: 'Revoke identity',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Partial',
        strength: 'Medium',
        gap: 'Clarify effective time, authority, and downstream notification handling.',
      },
      {
        area: 'Lifecycle management',
        component: 'Reason capture',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Partial',
        strength: 'Medium',
        gap: 'Standardize operational reason codes for future review and audit use.',
      },
      {
        area: 'Lifecycle management',
        component: 'Event timeline',
        linkTo: '/internal/identities',
        linkLabel: 'Identity timeline',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep the timeline readable and minimize any raw evidence exposure.',
      },
    ],
  },
  {
    id: 'audit-governance',
    letter: 'F',
    title: 'Audit and governance evidence',
    intro: 'Evidence that the platform-admin-only internal area stays separate from the normal workspace and uses safe metadata-only internal views.',
    items: [
      {
        area: 'Audit and governance',
        component: 'Platform-admin-only internal area',
        linkTo: '/internal',
        linkLabel: 'Open internal dashboard',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Preserve the /internal split and keep normal workspace routes outside platform-admin checks.',
      },
      {
        area: 'Audit and governance',
        component: 'Internal operations dashboard',
        linkTo: '/internal',
        linkLabel: 'Open dashboard',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep the dashboard on safe summary metadata and internal links only.',
      },
      {
        area: 'Audit and governance',
        component: 'Admin-only access controls',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep platform-admin gating strict and never reintroduce it into customer workspace APIs.',
      },
      {
        area: 'Audit and governance',
        component: 'Audit / event trail',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Inspect lab trail',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Continue using safe event metadata only.',
      },
      {
        area: 'Audit and governance',
        component: 'Governance pack',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Open governance pack',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Continue separating governance evidence from raw operational data.',
      },
      {
        area: 'Audit and governance',
        component: 'No raw evidence in overview',
        linkTo: '/internal/digital-key-accounts',
        linkLabel: 'Open accounts overview',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep overview pages limited to safe metadata only.',
      },
      {
        area: 'Audit and governance',
        component: 'Safe metadata only',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Open demo runner',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep the runbook free of personal or regulated identifiers.',
      },
    ],
  },
  {
    id: 'privacy-minimization',
    letter: 'G',
    title: 'Privacy and data minimization evidence',
    intro: 'Evidence items showing what stays out of scope and what still needs a formal privacy or retention decision.',
    items: [
      {
        area: 'Privacy and data minimization',
        component: 'No RRN/BIS in current scope',
        linkTo: '/internal/regulated-identifiers',
        linkLabel: 'Regulated identifiers',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep regulated identifier handling absent unless a separate legal path exists.',
      },
      {
        area: 'Privacy and data minimization',
        component: 'No raw identity evidence exposed in dashboards',
        linkTo: '/internal/digital-key-accounts',
        linkLabel: 'Open digital key accounts',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Continue showing only safe summaries and internal identifiers.',
      },
      {
        area: 'Privacy and data minimization',
        component: 'No document/selfie images exposed in evidence pack',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'BOSA evidence pack',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep the evidence pack narrative-only and never embed raw media.',
      },
      {
        area: 'Privacy and data minimization',
        component: 'No tokens / secrets / API keys exposed',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep the lab’s output safe and short-lived.',
      },
      {
        area: 'Privacy and data minimization',
        component: 'Image retention settings',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Needs BOSA/FAS validation',
        strength: 'Placeholder',
        gap: 'Document a retention setting matrix before any external privacy discussion.',
      },
      {
        area: 'Privacy and data minimization',
        component: 'GDPR / DPIA placeholder',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Draft',
        strength: 'Placeholder',
        gap: 'Prepare a DPIA outline and identify the owner before any formal review.',
      },
      {
        area: 'Privacy and data minimization',
        component: 'Data minimization notes',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'BOSA evidence pack',
        status: 'Implemented',
        strength: 'Medium',
        gap: 'Keep the summary text current as the internal lab evolves.',
      },
    ],
  },
  {
    id: 'technical-integration',
    letter: 'H',
    title: 'Technical integration readiness evidence',
    intro: 'Evidence that the internal lab and surrounding architecture are separated from normal workspace APIs and ready for future protocol discussion only.',
    items: [
      {
        area: 'Technical integration readiness',
        component: 'Relying party profiles',
        linkTo: '/internal/relying-party-profiles',
        linkLabel: 'Open relying parties',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Continue using safe relying-party metadata and internal test clients only.',
      },
      {
        area: 'Technical integration readiness',
        component: 'OIDC Lab simulator',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Internal lab',
        strength: 'Strong',
        gap: 'Keep the simulator internal-only and avoid production protocol claims.',
      },
      {
        area: 'Technical integration readiness',
        component: 'Authenticator Preview',
        linkTo: '/internal/authenticator-preview',
        linkLabel: 'Open authenticator preview',
        status: 'Internal lab',
        strength: 'Strong',
        gap: 'Continue to keep approve/deny flows and request returns internal only.',
      },
      {
        area: 'Technical integration readiness',
        component: 'Digital key architecture',
        linkTo: '/internal/digital-key-architecture',
        linkLabel: 'Open architecture',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep the architecture page as the source of truth for boundaries.',
      },
      {
        area: 'Technical integration readiness',
        component: 'Partner technical readiness',
        linkTo: '/internal/partner-technical-readiness',
        linkLabel: 'Open partner readiness',
        status: 'Needs BOSA/FAS validation',
        strength: 'Medium',
        gap: 'Validate partner assumptions and support expectations with BOSA/FAS.',
      },
      {
        area: 'Technical integration readiness',
        component: 'Route / API boundary separation',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Implemented',
        strength: 'Strong',
        gap: 'Keep internal routers inside /internal and out of normal workspace APIs.',
      },
    ],
  },
];

const openQuestions = [
  'What is the formal recognition path and scope for the internal digital key concept?',
  'What assurance level or LoA expectations should we prepare evidence for?',
  'Is a lab/demo flow useful before a formal application, and if so, what is the preferred shape?',
  'Can CSAM/FAS onboard one new digital key concept, or does it expect multiple login options?',
  'What technical protocol expectations should we prepare for first?',
  'Is a test environment available for early validation and integration discussions?',
  'What contractual or commercial model is expected for any future pilot or production discussion?',
  'What audit, security, and documentation package is required?',
  'What privacy and DPIA expectations should we plan for?',
  'What incident response and support requirements should we document early?',
];

const statusStyles = {
  Implemented: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  'Internal lab': 'border-sky-300 bg-sky-50 text-sky-800',
  Partial: 'border-amber-300 bg-amber-50 text-amber-800',
  Draft: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  'Needs BOSA/FAS validation': 'border-rose-300 bg-rose-50 text-rose-700',
  'Not started': 'border-zinc-200 bg-zinc-50 text-zinc-600',
  'Out of scope for MVP': 'border-violet-200 bg-violet-50 text-violet-700',
};

const strengthStyles = {
  Strong: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  Medium: 'border-amber-300 bg-amber-50 text-amber-800',
  Early: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  Placeholder: 'border-sky-200 bg-sky-50 text-sky-800',
};

function Badge({ value, styles, fallback = 'border-zinc-200 bg-zinc-50 text-zinc-600' }) {
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${styles[value] || fallback}`}>{value}</span>;
}

function SummaryCard({ label, value, detail }) {
  return (
    <article className="border border-zinc-200 bg-white p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-zinc-900">{value}</p>
      <p className="mt-2 text-sm leading-6 text-zinc-600">{detail}</p>
    </article>
  );
}

function EvidenceSection({ section }) {
  return (
    <section className="border border-zinc-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{section.letter}</p>
          <h2 className="mt-1 font-display text-lg font-semibold">{section.title}</h2>
          <p className="mt-2 max-w-4xl text-sm text-zinc-600">{section.intro}</p>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-[76rem] text-sm">
          <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="py-2 pr-4 font-medium">Evidence area</th>
              <th className="py-2 pr-4 font-medium">Current idemGO component</th>
              <th className="py-2 pr-4 font-medium">Internal link</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Evidence strength</th>
              <th className="py-2 font-medium">Gap / next action</th>
            </tr>
          </thead>
          <tbody>
            {section.items.map((item) => (
              <tr key={`${section.id}-${item.component}`} className="border-b border-zinc-100 last:border-b-0 align-top">
                <td className="py-3 pr-4 font-medium text-zinc-800">{item.area}</td>
                <td className="py-3 pr-4 text-zinc-600">{item.component}</td>
                <td className="py-3 pr-4">
                  <Link to={item.linkTo} className="text-zinc-900 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">
                    {item.linkLabel}
                  </Link>
                </td>
                <td className="py-3 pr-4">
                  <Badge value={item.status} styles={statusStyles} />
                </td>
                <td className="py-3 pr-4">
                  <Badge value={item.strength} styles={strengthStyles} />
                </td>
                <td className="py-3 text-zinc-600">{item.gap}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function BosaEvidencePack() {
  const summary = useMemo(() => {
    const items = evidenceSections.flatMap((section) => section.items);
    return {
      implemented: items.filter((item) => item.status === 'Implemented').length,
      internalLab: items.filter((item) => item.status === 'Internal lab').length,
      needsValidation: items.filter((item) => item.status === 'Needs BOSA/FAS validation').length,
      highPriorityGaps: items.filter((item) => ['Draft', 'Not started', 'Needs BOSA/FAS validation'].includes(item.status)).length,
    };
  }, []);

  return (
    <div data-testid="bosa-evidence-pack-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="BOSA / FAS Evidence Pack" showDashboard={false}/>

      <div className="mb-6">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Internal admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">BOSA / FAS Evidence Pack v0.2</h1>
        <p className="mt-2 max-w-4xl text-sm text-zinc-600">
          Internal evidence dossier for BOSA/FAS preparation. This is an internal evidence candidate and readiness preparation aid only.
        </p>
      </div>

      <section className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-medium">Internal evidence candidate only</p>
        <p className="mt-1">
          This page is not an official certification, approval, compliance, or LoA statement. It is a lab-only preparation dossier for internal review and BOSA/FAS validation.
        </p>
      </section>

      <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="Implemented evidence"
          value={summary.implemented}
          detail="Evidence items already demonstrated in current internal components."
        />
        <SummaryCard
          label="Internal lab evidence"
          value={summary.internalLab}
          detail="Evidence items that are explicitly part of the internal demo and lab runner."
        />
        <SummaryCard
          label="Needs validation"
          value={summary.needsValidation}
          detail="Items that still require BOSA/FAS validation or a formal decision."
        />
        <SummaryCard
          label="High-priority gaps"
          value={summary.highPriorityGaps}
          detail="Draft, not-started, or validation-dependent items that need follow-up first."
        />
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <h2 className="font-display text-lg font-semibold">Read this dossier</h2>
        <p className="mt-2 max-w-4xl text-sm text-zinc-600">
          Each row links to an internal page that demonstrates the current component. The wording stays intentionally careful: internal evidence candidate, readiness preparation, and needs BOSA/FAS validation only.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 text-sm">
          <Link to="/internal/digital-key-demo" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Digital Key Demo</Link>
          <Link to="/internal/fas-readiness" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">FAS Readiness</Link>
          <Link to="/internal/digital-key-architecture" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Digital Key Architecture</Link>
          <Link to="/internal/identities" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Identities</Link>
          <Link to="/internal/digital-key-accounts" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Digital Key Accounts</Link>
          <Link to="/internal/oidc-lab" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">OIDC Lab</Link>
          <Link to="/internal/authenticator-preview" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Authenticator Preview</Link>
          <Link to="/internal/audit-governance-pack" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Audit Governance</Link>
          <Link to="/internal/partner-technical-readiness" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Partner Readiness</Link>
          <Link to="/internal/relying-party-profiles" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Relying Party Profiles</Link>
          <Link to="/internal/regulated-identifiers" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Regulated Identifiers</Link>
        </div>
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <h2 className="font-display text-lg font-semibold">Executive readiness summary</h2>
        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          <article className="border border-zinc-100 p-4">
            <h3 className="text-sm font-medium text-zinc-800">What is implemented</h3>
            <p className="mt-2 text-sm text-zinc-600">
              idemGO already demonstrates approved-verification-to-identity creation, identity-to-digital-key-account linking, internal login request creation, authenticator approval, return-to-lab handoff, safe claims preview, and internal audit/governance views.
            </p>
          </article>
          <article className="border border-zinc-100 p-4">
            <h3 className="text-sm font-medium text-zinc-800">What remains draft or needs validation</h3>
            <p className="mt-2 text-sm text-zinc-600">
              The formal recognition path, exact BOSA/FAS assurance expectations, retention settings, privacy scope, integration expectations, and any future recognition route still need explicit validation.
            </p>
          </article>
        </div>
      </section>

      {evidenceSections.map((section) => (
        <div key={section.id} className="mt-6">
          <EvidenceSection section={section} />
        </div>
      ))}

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <section className="border border-zinc-200 bg-white p-5">
          <h2 className="font-display text-lg font-semibold">Open questions for BOSA / FAS</h2>
          <p className="mt-2 text-sm text-zinc-600">
            These are the decision points to confirm before this internal evidence can move beyond preparation.
          </p>
          <ol className="mt-4 space-y-2 text-sm text-zinc-600">
            {openQuestions.map((question) => (
              <li key={question} className="border-b border-zinc-100 pb-2 last:border-b-0">
                {question}
              </li>
            ))}
          </ol>
        </section>

        <section className="border border-zinc-200 bg-white p-5">
          <h2 className="font-display text-lg font-semibold">What this page does not claim</h2>
          <ul className="mt-4 space-y-2 text-sm text-zinc-600">
            <li>No official BOSA approval or official FAS/CSAM integration.</li>
            <li>No official certification, compliance statement, or LoA claim.</li>
            <li>No public login product, production OIDC, or regulated identifier handling.</li>
            <li>No raw evidence, images, selfies, OCR/MRZ raw data, tokens, or secrets are exposed here.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
